/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ErrorResponseModel, NotificationModel } from '../../../../../../core/models';
import { FormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import { LoadingService, LogService, NotifyService, UtilsService } from '../../../../../../core/services';
import { WizardService } from './wizard.service';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../../enums';
import { WizardTemplateService } from './wizardTemplate.service';
import { WizardStepModel } from '../../../models/wizardStep.model';

@Injectable()
export class LbErrorService {
    /**
     * Step Titles
     */
    public subStepTitles: any = [];

    /**
     * Inputs grouped by step title
     */
    public subStepInputsByTitle: any = {};

    /**
     * Initial Step State when wizard step loads
     */
    public currentStepName: string;

    /**
     * Form initial hidden state
     */
    public inputStateStore: any = {};

    /**
     * The main formGroup variable
     */
    private serviceForm: FormGroup;

    /**
     * List of validation subscribers to destroy
     * @private
     */
    private clearValidationSubscribers: any = [];

    /**
     * Flag to stop new form input subscriber from validating when stop has cleared notification
     * @private
     */
    private clearValidators: boolean = false;

    /**
     * Store the calling component title.
     * @private
     */
    private _componentTitle: string;

    /**
     * Flag to stop looping navigation when loading an error and adds class to form to force show validation errors.
     * @private
     */
    private _errorsLoaded: boolean;

    /**
     * Enables callback function to step through errors
     * @private
     */
    private errorStepCounter: number = 0;

    constructor(
        private loadingService: LoadingService,
        private logService: LogService,
        private notifyService: NotifyService,
        private utilsService: UtilsService,
        private wizardService: WizardService,
        private wizardTemplateService: WizardTemplateService,
    ) {
        this.wizardService.errorNotificationClosed
            .subscribe(
                state => {
                    if (state) {
                        this.clearValidators = true;
                        this.errorsLoaded = false;
                        this.clearValidationOnErroredFields();
                    }
                },
                error => {
                    this.logService.warn('LbErrorService:errorNotificationClosed: ', error.message);
                }
            );

        this.wizardTemplateService.templateChangeEmitter
            .subscribe(template => {
                if (!template && !this.wizardService.selectedTemplate) {
                    this.clearValidators = true;
                    this.errorsLoaded = false;
                    this.clearValidationOnErroredFields();
                    this.notifyService.removeNotificationById('wizardError');
                }
            });
    }

    /**
     * Set the component title for use in functions related to the step title
     * @param title
     */
    set componentTitle(title) {
        this._componentTitle = title;
    }

    /**
     * Get the component title for use in functions related to the step title
     */
    get componentTitle(): string {
        return this._componentTitle;
    }

    /**
     * Enable the errors loaded state to be set outside the service
     * @param state
     */
    set errorsLoaded(state: boolean) {
        this._errorsLoaded = state;
    }

    /**
     * Gets the errors loaded state.
     */
    get errorsLoaded() {
        return this._errorsLoaded;
    }

    /**
     * Set up the class variables for use with error checking
     * @param classVariables
     */
    public setClassVariables(classVariables: any): void {
        const {
            componentTitle,
            form,
            subStepTitles,
            subStepInputsByTitle,
            inputStateStore,
            currentStepName
        } = classVariables;
        this.componentTitle = componentTitle;
        this.serviceForm = form;
        this.subStepTitles = subStepTitles;
        this.subStepInputsByTitle = subStepInputsByTitle;
        this.inputStateStore = inputStateStore;
        this.currentStepName = currentStepName;
    }

    /**
     * For use with single page error response message.
     * @param form
     * @param errorArray
     * @param errorUId
     */
    public pageWithErrors(form: any, errorArray: any, errorUId?: string): void {
        this.serviceForm = form;
        this.formatErrors(errorArray, errorUId);
    }

    /**
     * Checks if the fields in the error array are hidden and then runs the error helper.
     * @param errorArray
     */
    public loadedWithErrors(errorArray: any[]): void {
        if (!this.errorsLoaded) {
            this.errorsLoaded = true;
            if (this.errorsAreHidden(errorArray)) {
                // If errors are hidden it is assumed that they are advanced fields so enable advanced mode.
                this.wizardService.wizardModeEmitter.next('advanced');
            }

            this.errorHelper(errorArray);
        }
    }

    /**
     * Check to see if any fields are hidden.
     * @param errorArray
     * @private
     */
    private errorsAreHidden(errorArray: any[]): boolean {
        return errorArray.some(error => this.inputStateStore[error.field] && this.inputStateStore[error.field].hidden);
    }

    /**
     * Get the floating ip id from the error message.
     * @param errorArray
     */
    public getFloatingIpValidationFromErrorObject(errorArray: any[]): ErrorResponseModel {
        const errorHasFipId = errorArray.filter(error => error.field === 'fipId');
        if (errorHasFipId && errorHasFipId.length) {
            return errorHasFipId[0];
        }
    }

    /**
     * Get the floating ip id from the error message.
     * @param errorArray
     */
    public getSSLCertValidationFromErrorObject(errorArray: any[]): ErrorResponseModel {
        const errorHasSSLCertId = errorArray.filter(error => error.field === 'certificateId' || error.field === 'cipherListId');
        if (errorHasSSLCertId && errorHasSSLCertId.length) {
            return errorHasSSLCertId[0];
        }
    }

    /**
     * Helper function to kick off setting new errors
     * @param errorArray
     * @private
     */
    private errorHelper(errorArray: any[]): void {
        const formattedErrors: any[] = this.formatErrors(errorArray);

        this.navigateToFirstSubStepThatFailed(formattedErrors);
        this.setNotificationMessage(formattedErrors);
    }

    /**
     * Format the errors with index and section, and sort them for navigation.
     * @param errorArray
     * @param errorUId
     * @private
     */
    private formatErrors(errorArray: any, errorUId?: string): any [] {
        const formattedErrors = [];
        for (const error of errorArray) {
            const sectionName = this.getErroredFieldSection(error.field);
            const index = sectionName ? this.getErroredSubStepIndex(sectionName) : null;
            const formInputPath = sectionName ? `${sectionName}.${error.field}` : error.field;
            const formattedError = {
                index,
                sectionName,
                field: error.field.toLowerCase(),
                message: error.message,
                type: error.type,
                oldValue: error.value,
                formInputPath: formInputPath
            };

            this.validateErroredField(formattedError, errorUId);
            formattedErrors.push(formattedError);
        }

        return this.utilsService.sortArrayByProperty(formattedErrors, 'index');
    }

    /**
     * Get errored field section name.
     * @param field
     * @private
     */
    private getErroredFieldSection(field: string): string {
        for (const property in this.subStepInputsByTitle) {
            if (this.subStepInputsByTitle.hasOwnProperty(property)) {
                let cleanProperty: string;
                let foundTitle: string | any[];
                if (field === 'cipherListId' || field === 'certificateId') {
                    cleanProperty = WizardSubStepTitlesEnum.CERTIFICATES;
                    foundTitle = this.subStepInputsByTitle[cleanProperty];
                } else {
                    cleanProperty = property;
                    foundTitle = this.subStepInputsByTitle[cleanProperty].filter((title: string) =>
                        title.toLowerCase() === field.toLowerCase()
                    );
                }
                if (foundTitle.length) {
                    return cleanProperty;
                }
            }
        }
    }

    /**
     * Get step index from step title
     * @param subStepTitle
     * @private
     */
    private getErroredSubStepIndex(subStepTitle: string): number {
        const parentStep = this.wizardService.wizardSteps[this.wizardService.activeStepIndex];
        const parentStepIndex = this.wizardService.wizardSteps.findIndex(item => item.title === parentStep.title);

        if (subStepTitle === 'certificates') {
            return parentStep.subSteps.findIndex((subStep: WizardStepModel) => subStep.name === subStepTitle);
        } else {
            const visibleSubSteps = parentStep.subSteps.filter((subStep: WizardStepModel) => subStep.isVisible);
            const erroredSubStepIndex = visibleSubSteps.findIndex((subStep: WizardStepModel) => subStep.name === subStepTitle);
            const erroredStepObj = {
                parentIndex: parentStepIndex,
                subStepIndex: erroredSubStepIndex
            };
            this.wizardService.erroredSteps.push(erroredStepObj);
            visibleSubSteps[erroredSubStepIndex].isErrored = true;

            return erroredSubStepIndex;
        }
    }

    /**
     * Sets up validation on the errored field.
     * @param error
     * @param errorUId
     * @private
     */
    private validateErroredField(error: any, errorUId?: string): void {
        const field = this.serviceForm.get(error.formInputPath);
        if (!field) {
            this.addNotification(error, errorUId);
            this.logService.warn('LbErrorService:validateErroredField', `Input ${error.field} could not be found`);
        } else {
            field.markAsDirty();
            field.markAsTouched();
            field.setErrors({ 'responseError': error.message });

            this.clearValidationSubscribers[error.formInputPath] = field.valueChanges
                .subscribe({
                    next: value => {
                        const {
                            formattedFormValue,
                            formattedErrorValue
                        } = this.formatValuesForCompare(value, error.oldValue);

                        if (!this.clearValidators) {
                            if (formattedFormValue === formattedErrorValue) {
                                field.setErrors({ 'responseError': error.message });
                                field.markAsDirty();
                                field.markAsTouched();
                            } else if (field.hasError('responseError')) {
                                field.setErrors(null);
                            }

                            field.updateValueAndValidity({ emitEvent: false });
                        }
                    }
                });
        }
    }

    /**
     * Format values for compare.
     * @param value
     * @param errorValue
     * @private
     */
    private formatValuesForCompare(value: any, errorValue: any): any {
        if (Array.isArray(value)) {
            const formattedFormValue = JSON.stringify(value);
            const formattedErrorValue = JSON.stringify(JSON.parse(errorValue));

            return { formattedFormValue, formattedErrorValue };
        } else {
            return { formattedFormValue: value.toString(), formattedErrorValue: errorValue.toString() };
        }
    }

    /**
     * Add notification if we can't validate a field.
     * @param error
     * @param errorUId
     * @private
     */
    private addNotification(error: any, errorUId: string): void {
        const notifications = this.notifyService.getCopyOfNotifications();
        const matchedIndex = notifications.findIndex(item => item.errorId === errorUId || item.id === errorUId);
        if (matchedIndex !== -1) {
            const matchedNotification = this.notifyService.getNotificationsById(notifications[matchedIndex].id);
            const noMessageMatch = !matchedNotification.message.includes(error.message);
            if (matchedNotification && noMessageMatch) {
                const concatMessage = matchedNotification.message + '. ' + error.message;
                matchedNotification.title = concatMessage;
                matchedNotification.message = concatMessage;
            } else {
                this.logService.error('LbErrorService: addNotification', 'Error matching messages and notifications');
            }
        } else if (this.errorAlreadyExists(notifications, error.message)) {
            this.notifyService.error(error.message, { errorId: errorUId });
        } else {
            this.logService.error('LbErrorService: addNotification', 'Error matching anything.');
        }
    }

    /**
     * Check if the error message supplied already exists in the notifications array.
     * @param notifications
     * @param errorMessage
     * @private
     */
    private errorAlreadyExists(notifications: NotificationModel[], errorMessage: string): boolean {
        return notifications.some(notify => notify.message === errorMessage);
    }

    /**
     * Navigate user to the first error.
     * @param formattedErrors
     * @private
     */
    private navigateToFirstSubStepThatFailed(formattedErrors: any[]): void {
        const parentStepIndex = this.wizardService.activeStepIndex;
        if (formattedErrors[0].index !== -1) {
            this.wizardService.goToStep(parentStepIndex, formattedErrors[0].index);
        }
    }

    /**
     * User option to clear the validators because they have made changes that fix the error(s).
     * @private
     */
    private clearValidationOnErroredFields(): void {
        const componentTitle = this.componentTitle === 'Monitors' ? WizardTitleEnum.FRONTENDS : this.componentTitle;
        const errors = this.utilsService.getObjectValue(
            this.wizardService.controllerState, `errorState.${componentTitle}`);

        if (errors && errors.length) {
            for (const error of errors) {
                this.removeErrors(error);
            }

            delete this.wizardService.controllerState.errorState;
            this.clearValidationSubscribers = [];
        }

        // Reset flag in case the user doesn't fix issue
        this.clearValidators = false;
        this.errorsLoaded = false;

        this.logService.debug(
            'LbErrorService: clearValidationOnErroredFields',
            'Cleared validators'
        );
    }

    /**
     * Clean up errors
     * @param error
     * @private
     */
    private removeErrors(error: { field: string; }): void {
        const sectionName = this.getErroredFieldSection(error.field);
        const formInput = `${sectionName}.${error.field}`;
        const field = this.serviceForm.get(formInput);

        if (field && field.hasError('responseError')) {
            this.logService.debug('LbErrorService: clearValidationOnErroredFields',
                `Removing error on ${formInput}.`);
            field.setErrors({ 'responseError': null });
            field.updateValueAndValidity({ onlySelf: false, emitEvent: true });
        } else {
            this.logService.warn('LbErrorService: clearValidationOnErroredFields',
                `Can't find field ${formInput} to remove its error message.`);
        }

        // Remove the subscriber to stop it validating.
        if (this.clearValidationSubscribers[formInput]) {
            this.clearValidationSubscribers[formInput].unsubscribe();
        } else {
            this.logService.warn(
                'LbErrorService: clearValidationOnErroredFields',
                'Subscriber not found and therefore couldn\'t be removed'
            );
        }
    }

    /**
     * Create a message from the error message and display it to the user.
     * @param formattedErrors
     * @private
     */
    private setNotificationMessage(formattedErrors: any[]): void {
        const missingIndex = formattedErrors.filter(error => error.index === -1);
        if (missingIndex.length) {
            const errorNotification = this.formatMissingContentErrors(missingIndex);
            this.notifyService.error(errorNotification, { id: 'wizardError', wizardError: 'wizardError' });
        } else if (formattedErrors.length) {
            const sections = formattedErrors.map(error => error.sectionName);
            const dedupedSections = this.utilsService.dedupe(sections);

            if (formattedErrors.length === 1 || dedupedSections.length === 1) {
                this.notifyService.error(
                    `The wizard was unable to configure the deployment specified.
                     Please fix the error. ${formattedErrors[0].message}.`,
                    { id: 'wizardError', wizardError: 'wizardError' }
                );
            } else {
                const callBack = this.callBackHelper(formattedErrors);
                const fields = formattedErrors.map(error => error.field);
                // Convert array to string and add extra space after comma.
                const additionalMessage = fields.toString().replace(/,(?=[^,]*$)/, ' and ');
                this.notifyService.error(
                    `The wizard was unable to configure the deployment specified.
                 There are ${formattedErrors.length} errors that need addressing. Fields: ${additionalMessage}`,
                    { callBack, id: 'wizardError', wizardError: 'wizardError' }
                );
            }
        }
    }

    /**
     * Format messages for errors with missing indexes
     * @param missingInputs
     * @private
     */
    private formatMissingContentErrors(missingInputs: any[]): string {
        let errorNotification = '';

        if (missingInputs.length === 1) {
            errorNotification = `The wizard was unable to configure the deployment specified. ${missingInputs[0].message}`;
        } else {
            const messages = missingInputs.map(item => item.message);
            const dedupedMessages = this.utilsService.dedupe(messages);
            for (const message of dedupedMessages) {
                const addedBreak = message.replace('. ', '.<br>');
                errorNotification = errorNotification + addedBreak + '<br>';
            }
        }

        return errorNotification;
    }

    /**
     * Set up a callback button in the notification.
     * @param formattedErrors
     * @private
     */
    private callBackHelper(formattedErrors: any[]): any {
        return {
            text: 'wizard_step_errors',
            type: 'error',
            function: this.nextError.bind(this, formattedErrors)
        };
    }

    /**
     * Navigates to the next error in the formattedErrors array.
     * @param formattedErrors
     * @private
     */
    private nextError(formattedErrors: string | any[]): void {
        // Increment the step counter until it is the same as the errors length
        this.errorStepCounter = this.errorStepCounter++ === formattedErrors.length - 1
            ? 0
            : this.errorStepCounter++;

        this.wizardService.goToStep(this.wizardService.activeStepIndex, formattedErrors[this.errorStepCounter].index);
        this.loadingService.loadingButtonEmitter.next(false);
    }
}
